<template lang="pug">
#waiter-setting(v-cloak)
  .box
    .box-header
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .title
            span 入所者募集・入所申込者管理の設定
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.back-btn(type="button" @click="gotoWaiterList") 戻る
    .box-body
      b-tabs
        //- facility setting
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span 入所者募集ページ
          FacilitySettingTab(ref="facilitySettingTab" v-model="facility" :errorMessage="errorMessage"
            @onRegister="registerWaiterRecruitment" @onUpdate="updateWaiterRecruitment")
        //- application template select
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span 入所申込み様式
          ApplicationTemplateSelectTab(ref="applicationTemplateSelectTab" v-model="facility" :errorMessage.sync="applicationErrorMessage" @update="confirmUpdateApplication")
        //- judgement template select
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span 入所評価判定様式
          JudgementTemplateSelectTab(ref="judgementTemplateSelectTab" v-model="facility" :errorMessage.sync="judgementErrorMessage" @update="confirmUpdateJudgement")
        //- room type setting
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span 部屋種類
          RoomTypeSettingTab(ref="roomTypeSettingTab" v-model="facility" :errorMessage.sync="roomTypeErrorMessage" @update="confirmUpdateRoomType")
        //- label setting
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span ラベル編集
          WaiterLabelSettingTab(ref="waiterLabelSettingTab" :errorMessage="errorMessageInWaiterLabelsUpdate" @onUpdate="updateWaiterLabels")

  CheckDeleteModal(v-model="showApplicationUpdateModal" title="入所申込み様式を変更しますか？" @ok="updateApplicationTemplate")
    template(#message)
      .message 変更前に以下をご確認ください。
      ul.confirm-list
        li すでに申込みされている申込者の様式は変更されません。
  CheckDeleteModal(v-model="showJudgementUpdateModal" title="入所評価判定様式を変更しますか？" @ok="updateJudgementTemplate")
    template(#message)
      .message 変更前に以下をご確認ください。
      ul.confirm-list
        li すでに申込みされている申込者の様式は変更されません。
  OkCancelModal(v-model="showRoomTypeUpdateModal" title=" 入所申込みの部屋種類を変更しますか？" @ok="updateRoomType")
  OkModal(v-model="showSuccessModal" :title="successModalTitle")

  //- file upload error modal
  FileUploadErrorModal(v-model="showUploadErrorModal")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  ProgressLoading(v-model="currentUploadCount" :max="uploadCount" :isBusy="progressLoading" :message="progressMessage")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";
import { getType } from "mime";

import FacilitiesService, { FacilityGetModel, FacilityPatchModel } from "@/commons/services/Facilities";
import WaiterRecruitmentsService, {
  Media,
  Document,
  WaiterRecruitmentsPostModel,
  WaiterRecruitmentsPatchModel,
  BasicInformation,
  HeaderImage
} from "@/commons/services/WaiterRecruitments";
import S3LocationsService from "@/commons/services/S3Locations";
import { S3LocationRequest, S3LocationResponse, S3LocationType } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";
import WaiterLabelsService, { WaiterLabelsPatchModel } from "@/commons/services/WaiterLabels";

import FacilitySettingTab, {
  RegisterWaiterRecruitmentModel,
  UpdateWaiterRecruitmentModel,
  WaiterRecruitmentBasicInformation
} from "@/pc/containers/waiters/FacilitySettingTab.vue";
import ApplicationTemplateSelectTab from "@/pc/containers/waiters/ApplicationTemplateSelectTab.vue";
import JudgementTemplateSelectTab from "@/pc/containers/waiters/JudgementTemplateSelectTab.vue";
import RoomTypeSettingTab from "@/pc/containers/waiters/RoomTypeSettingTab.vue";
import WaiterLabelSettingTab from "@/pc/containers/waiters/WaiterLabelSettingTab.vue";

import FileUploadErrorModal from "@/pc/basics/FileUploadErrorModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import CheckDeleteModal from "@/pc/components/modals/CheckDeleteModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ProgressLoading from "@/pc/basics/ProgressLoading.vue";
import SystemError from "@/commons/models/SystemError";
import { WaiterLabelSettingTableItem } from "@/pc/containers/waiters/WaiterLabelSettingTable.vue";

/**
 * ### 待機者設定画面コンポーネント
 */
@Component({
  components: {
    FileUploadErrorModal,
    FacilitySettingTab,
    ApplicationTemplateSelectTab,
    JudgementTemplateSelectTab,
    RoomTypeSettingTab,
    WaiterLabelSettingTab,
    OkModal,
    OkCancelModal,
    CheckDeleteModal,
    PageLoading,
    ProgressLoading
  }
})
export default class WaiterSetting extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 待機者募集ページ */
  @Inject("WaiterRecruitmentsService") waiterRecruitmentsService!: WaiterRecruitmentsService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;
  /** 待機者ラベルサービス */
  @Inject("WaiterLabelsService") waiterLabelsService!: WaiterLabelsService;

  /** 入居募集ページタブ */
  @Ref() facilitySettingTab!: FacilitySettingTab;
  /** 入居申込みテンプレート設定タブ */
  @Ref() applicationTemplateSelectTab!: ApplicationTemplateSelectTab;
  /** 入居判定テンプレート設定タブ */
  @Ref() judgementTemplateSelectTab!: JudgementTemplateSelectTab;
  /** 待機者ラベル設定タブ */
  @Ref() waiterLabelSettingTab!: WaiterLabelSettingTab;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 現在のファイルアップロードファイル数 */
  currentUploadCount: number = 0;
  /** ファイルアップロードファイル数(母数) */
  uploadCount: number = 0;
  /** プログレスローディングのメッセージ */
  progressMessage: string = "";
  /** プログレスローディングの表示可否 */
  progressLoading: boolean = false;

  /** 更新対象の施設情報 */
  facility: FacilityGetModel | null = null;

  /** 入所申込テンプレート更新確認モーダル表示可否 */
  showApplicationUpdateModal: boolean = false;
  /** 入所判定テンプレート更新確認モーダル表示可否 */
  showJudgementUpdateModal: boolean = false;
  /** 部屋種類更新確認モーダル表示可否 */
  showRoomTypeUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showSuccessModal: boolean = false;
  /** 更新完了モーダルタイトル */
  successModalTitle: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";
  /** 入所申込テンプレートエラーメッセージ */
  applicationErrorMessage: string = "";
  /** 入所判定テンプレートエラーメッセージ */
  judgementErrorMessage: string = "";
  /** 部屋種類エラーメッセージ */
  roomTypeErrorMessage: string = "";
  /** 待機者ラベル更新エラーメッセージ */
  errorMessageInWaiterLabelsUpdate: string = "";
  /** ファイルアップロードエラーモーダル表示可否 */
  showUploadErrorModal: boolean = false;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const facility: FacilityGetModel = await this.getFacility();
    this.facility = facility;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設取得
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * ### 各タブ情報を再表示する
   */
  public async reflesh(): Promise<void> {
    await this.createView();
    this.facilitySettingTab.refresh();
    this.waiterLabelSettingTab.refresh();
    return;
  }

  /**
   * ### 入所募集ページを登録する
   */
  public async registerWaiterRecruitment(model: RegisterWaiterRecruitmentModel): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    try {
      const requestBody: WaiterRecruitmentsPostModel = await this.createRegisterWaiterRecruitmentRequestBody(model);
      await this.waiterRecruitmentsService.post(userStore, requestBody);

      this.successModalTitle = "登録に成功しました。";
      this.showSuccessModal = true;

      await this.reflesh();
      this.isBusy = false;
    } catch (error) {
      const _error: SystemError = error as SystemError;
      // ファイルアップロード失敗の場合はモーダルを表示して再入力させる
      if (_error.code == "ERR_NETWORK") {
        this.isBusy = false;

        this.showUploadErrorModal = true;
        this.facilitySettingTab.resetFiles();
        return;
      }

      throw error;
    }

    return;
  }

  /**
   * ### 待機者登録用リクエストボディを作成する
   */
  public async createRegisterWaiterRecruitmentRequestBody(model: RegisterWaiterRecruitmentModel): Promise<WaiterRecruitmentsPostModel> {
    const headerImages: Array<HeaderImage> = await this.uploadWaiterRecruitmentHeaderImages(model);
    const basicInformation: BasicInformation = this.createBasicInformation(model.basicInformation, headerImages);
    const updatedMedia: Array<Media> = await this.uploadWaiterRecruitmentMedia(model);
    const updatedDocuments: Array<Document> = await this.uploadWaiterRecruitmentDocuments(model);

    const requestBody: WaiterRecruitmentsPostModel = {
      ...model,
      basicInformation: basicInformation,
      media: updatedMedia,
      documents: updatedDocuments
    };

    return requestBody;
  }

  /**
   * ### 待機者ヘッダー画像をアップロードしてリクエスト情報を作成して返す
   */
  public async uploadWaiterRecruitmentHeaderImages(model: RegisterWaiterRecruitmentModel): Promise<Array<HeaderImage>> {
    let uploadHeaderImages: Array<HeaderImage> = [];

    if (0 < model.basicInformation.headerImageFiles.length) {
      let files: Array<File> = [];
      for (const headerImageFile of model.basicInformation.headerImageFiles) {
        if (headerImageFile.file) {
          // URLが存在しない場合はアップロード対象
          if (!headerImageFile.url) {
            const mime: string | null = this.getType(headerImageFile.file.name);
            switch (mime) {
              case "image/jpeg":
              case "image/png":
              case "video/mp4":
              case "video/quicktime": {
                files.push(headerImageFile.file as File);
                break;
              }
            }
          }
          // URLが存在する場合は登録済みのファイルなのでアップロード対象外とし、ファイル名やURL情報を登録する
          else {
            uploadHeaderImages.push({
              name: headerImageFile.name,
              url: headerImageFile.url,
              type: headerImageFile.type
            });
          }
        }
      }

      if (0 < files.length) {
        try {
          this.currentUploadCount = 0;
          this.uploadCount = files.length;
          this.progressMessage = "ヘッダー画像ファイルアップロード中。\n完了するまでそのままお待ちください。\nアップロード中に別画面に遷移しますと処理がキャンセルされます。";
          this.progressLoading = true;

          const urls: Array<S3LocationResponse> = await this.createUploadUrls(files, S3LocationType.WAITER_RECRUITMENT_HEADERIMAGE);
          for (let i = 0; i < urls.length; i++) {
            this.currentUploadCount = i;
            await this.s3UploadsService.uploadFile(urls[i], files[i]);

            // DBにはファイルではなく、添付ファイルのS3URLを保持するためURL配列を作成する
            const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
            const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;

            uploadHeaderImages.push({
              name: files[i].name,
              url: s3Url,
              type: files[i].type
            });
          }
        } finally {
          this.progressLoading = false;
        }
      }
    }

    return uploadHeaderImages;
  }

  /**
   * ### 待機者基本情報を作成する
   */
  public createBasicInformation(waiterRecruitmentbasicInformation: WaiterRecruitmentBasicInformation, headerImages: Array<HeaderImage>): BasicInformation {
    return {
      facilityName: waiterRecruitmentbasicInformation.facilityName,
      headerImages: headerImages,
      facilityType: waiterRecruitmentbasicInformation.facilityType,
      address: waiterRecruitmentbasicInformation.address,
      facilityUrl: waiterRecruitmentbasicInformation.facilityUrl,
      corporationName: waiterRecruitmentbasicInformation.corporationName,
      tel: waiterRecruitmentbasicInformation.tel,
      twitterUrl: waiterRecruitmentbasicInformation.twitterUrl,
      facebookUrl: waiterRecruitmentbasicInformation.facebookUrl,
      instagramUrl: waiterRecruitmentbasicInformation.instagramUrl,
      corporationUrl: waiterRecruitmentbasicInformation.corporationUrl
    };
  }

  /**
   * ### 待機者画像・動画ファイルをアップロードしてリクエスト情報を作成して返す
   */
  public async uploadWaiterRecruitmentMedia(model: RegisterWaiterRecruitmentModel): Promise<Array<Media>> {
    let uploadMedia: Array<Media> = [];

    if (0 < model.media.length && model.showSettings.media == true) {
      let files: Array<File> = [];
      let descriptions: string[] = [];
      for (const media of model.media) {
        if (media.file) {
          // URLが存在しない場合はアップロード対象
          if (!media.url) {
            const mime: string | null = this.getType(media.file.name);
            switch (mime) {
              case "image/jpeg":
              case "image/png":
              case "video/mp4":
              case "video/quicktime": {
                files.push(media.file as File);
                descriptions.push(media.description);
                break;
              }
            }
          }
          // URLが存在する場合は登録済みのファイルなのでアップロード対象外とし、ファイル名やURL情報を登録する
          else {
            uploadMedia.push({
              name: media.name,
              url: media.url,
              type: media.type,
              description: media.description
            });
          }
        }
      }

      if (0 < files.length) {
        try {
          this.currentUploadCount = 0;
          this.uploadCount = files.length;
          this.progressMessage = "動画・画像ファイルアップロード中。\n完了するまでそのままお待ちください。\nアップロード中に別画面に遷移しますと処理がキャンセルされます。";
          this.progressLoading = true;

          const urls: Array<S3LocationResponse> = await this.createUploadUrls(files, S3LocationType.WAITER_RECRUITMENT_MEDIA);
          for (let i = 0; i < urls.length; i++) {
            this.currentUploadCount = i;
            await this.s3UploadsService.uploadFile(urls[i], files[i]);

            // DBにはファイルではなく、添付ファイルのS3URLを保持するためURL配列を作成する
            const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
            const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;

            uploadMedia.push({
              name: files[i].name,
              url: s3Url,
              type: files[i].type,
              description: descriptions[i]
            });
          }
        } finally {
          this.progressLoading = false;
        }
      }
    }

    return uploadMedia;
  }

  /**
   * ### S3へファイルアップロードするURL情報を作成する
   */
  public async createUploadUrls(files: Array<File>, s3LocationType: S3LocationType): Promise<Array<S3LocationResponse>> {
    let extensions: Array<string> = [];

    for (const file of files) {
      const mime: string | null = this.getType(file.name);
      switch (mime) {
        case "image/jpeg": {
          extensions.push(".jpeg");
          break;
        }
        case "image/png": {
          extensions.push(".png");
          break;
        }
        case "video/mp4": {
          extensions.push(".mp4");
          break;
        }
        case "video/quicktime": {
          extensions.push(".mov");
          break;
        }
        case "application/pdf": {
          extensions.push(".pdf");
          break;
        }
      }
    }

    const requestBody: S3LocationRequest = {
      type: s3LocationType,
      count: files.length,
      extensions: extensions,
      params: {
        userId: userStore.loginUser.userId
      },
      acl: "public-read"
    };
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return url;
  }

  /**
   * ## ファイルのパスまたは拡張子からContent-Typeを取得する
   */
  public getType(fileString: string): string | null {
    /**
     * getTypeの引数にはファイルパスが必要
     * 先頭文字列が「.」の場合、拡張子でありファイルパスではないため、先頭にファイル名を付けてファイルパスにする
     * 先頭文字列が「.」ではない場合、ファイルパスとする
     */
    let contentType: string | null = "";
    if (fileString[0] == ".") {
      contentType = getType(`file${fileString}`);
    } else {
      contentType = getType(`${fileString}`);
    }

    return contentType;
  }

  /**
   * ### 待機者ドキュメントファイルをアップロードしてリクエスト情報を作成して返す
   */
  public async uploadWaiterRecruitmentDocuments(model: RegisterWaiterRecruitmentModel): Promise<Array<Document>> {
    let uploadDocuments: Array<Document> = [];

    if (0 < model.documents.length && model.showSettings.documents == true) {
      let files: Array<File> = [];
      let linkTexts: string[] = [];
      for (const document of model.documents) {
        if (document.file) {
          // URLが存在しない場合はアップロード対象
          if (!document.url) {
            const mime: string | null = this.getType(document.file.name);
            switch (mime) {
              case "application/pdf": {
                files.push(document.file as File);
                linkTexts.push(document.linkText);
                break;
              }
            }
          }
          // URLが存在する場合は登録済みのファイルなのでアップロード対象外とし、ファイル名やURL情報を登録する
          else {
            uploadDocuments.push({
              name: document.name,
              url: document.url,
              linkText: document.linkText
            });
          }
        }
      }

      if (0 < files.length) {
        try {
          this.currentUploadCount = 0;
          this.uploadCount = model.documents.length;
          this.progressMessage = "資料ファイルアップロード中。\n完了するまでそのままお待ちください。\nアップロード中に別画面に遷移しますと処理がキャンセルされます。";
          this.progressLoading = true;

          const urls: Array<S3LocationResponse> = await this.createUploadUrls(files, S3LocationType.WAITER_RECRUITMENT_DOCUMENT);

          for (let i = 0; i < urls.length; i++) {
            this.currentUploadCount = i;
            await this.s3UploadsService.uploadFile(urls[i], files[i]);

            // DBにはファイルではなく、添付ファイルのS3URLを保持するためURL配列を作成する
            const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
            const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;

            uploadDocuments.push({
              name: files[i].name,
              url: s3Url,
              linkText: linkTexts[i]
            });
          }
        } finally {
          this.progressLoading = false;
        }
      }
    }

    return uploadDocuments;
  }

  /**
   * ### 入所募集ページを更新する
   */
  public async updateWaiterRecruitment(model: UpdateWaiterRecruitmentModel): Promise<void> {
    try {
      this.isBusy = true;
      this.loadingMessage = "更新中...";

      try {
        const requestBody: WaiterRecruitmentsPatchModel = await this.createUpdateWaiterRecruitmentRequestBody(model);
        await this.waiterRecruitmentsService.patch(userStore, requestBody);
      } catch (error) {
        const _error: SystemError = error as SystemError;
        // ファイルアップロード失敗の場合はモーダルを表示して再入力させる
        if (_error.code == "ERR_NETWORK") {
          this.isBusy = false;

          this.showUploadErrorModal = true;
          this.facilitySettingTab.resetFiles();
          return;
        }

        throw error;
      }

      this.successModalTitle = "更新に成功しました。";
      this.showSuccessModal = true;

      await this.reflesh();
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        this.errorMessageInWaiterLabelsUpdate = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        await this.reflesh();
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 待機者登録用リクエストボディを作成する
   */
  public async createUpdateWaiterRecruitmentRequestBody(model: UpdateWaiterRecruitmentModel): Promise<WaiterRecruitmentsPatchModel> {
    const headerImages: Array<HeaderImage> = await this.uploadWaiterRecruitmentHeaderImages(model);
    const basicInformation: BasicInformation = this.createBasicInformation(model.basicInformation, headerImages);
    const updatedMedia: Array<Media> = await this.uploadWaiterRecruitmentMedia(model);
    const updatedDocuments: Array<Document> = await this.uploadWaiterRecruitmentDocuments(model);

    const requestBody: WaiterRecruitmentsPatchModel = {
      ...model,
      basicInformation: basicInformation,
      media: updatedMedia,
      documents: updatedDocuments
    };

    return requestBody;
  }

  /**
   * ### 入所申込みテンプレート更新確認モーダルを表示する
   */
  public confirmUpdateApplication(): void {
    this.showApplicationUpdateModal = true;
    return;
  }

  /**
   * ### 使用する入所申込みテンプレートを更新する
   */
  public async updateApplicationTemplate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "変更中...";

    try {
      const facility: FacilityGetModel = this.facility as FacilityGetModel;
      let requestBody: FacilityPatchModel = {
        address: facility.address,
        name: facility.name,
        tel: facility.tel,
        url: facility.url,
        useAdmissionApplicationTemplateId: facility.useAdmissionApplicationTemplateId,
        useAdmissionJudgementTemplateId: facility.useAdmissionJudgementTemplateId,
        recruitmentRoom: facility.recruitmentRoom,
        updated: facility.updated
      };

      await this.facilitiesService.patch(userStore, requestBody);

      this.successModalTitle = "変更に成功しました。";
      this.showSuccessModal = true;
      await this.createView();
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.applicationErrorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 入所判定テンプレート更新確認モーダルを表示する
   */
  public confirmUpdateJudgement(): void {
    this.showJudgementUpdateModal = true;
    return;
  }

  /**
   * ### 使用する入所判定テンプレートを更新する
   */
  public async updateJudgementTemplate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "変更中...";

    try {
      const facility: FacilityGetModel = this.facility as FacilityGetModel;
      let requestBody: FacilityPatchModel = {
        address: facility.address,
        name: facility.name,
        tel: facility.tel,
        url: facility.url,
        useAdmissionApplicationTemplateId: facility.useAdmissionApplicationTemplateId,
        useAdmissionJudgementTemplateId: facility.useAdmissionJudgementTemplateId,
        recruitmentRoom: facility.recruitmentRoom,
        updated: facility.updated
      };

      await this.facilitiesService.patch(userStore, requestBody);

      this.successModalTitle = "変更に成功しました。";
      this.showSuccessModal = true;
      await this.createView();
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.judgementErrorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  public confirmUpdateRoomType(): void {
    this.showRoomTypeUpdateModal = true;
    return;
  }

  /**
   * ### 施設が使用する部屋種類を更新する
   */
  public async updateRoomType(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "変更中...";

    try {
      const facility: FacilityGetModel = this.facility as FacilityGetModel;
      let requestBody: FacilityPatchModel = {
        address: facility.address,
        name: facility.name,
        tel: facility.tel,
        url: facility.url,
        useAdmissionApplicationTemplateId: facility.useAdmissionApplicationTemplateId,
        useAdmissionJudgementTemplateId: facility.useAdmissionJudgementTemplateId,
        recruitmentRoom: facility.recruitmentRoom,
        customRecruitmentRoom: facility.customRecruitmentRoom,
        updated: facility.updated
      };

      await this.facilitiesService.patch(userStore, requestBody);

      this.successModalTitle = "変更に成功しました。";

      // 施設情報を更新したので他タブに影響ある情報はstoreを更新する
      userStore.facility.recruitmentRoom = facility.recruitmentRoom;
      userStore.facility.customRecruitmentRoom = facility.customRecruitmentRoom;
      // カスタム部屋が更新されると料金シミュレーションの値の変更があるので、読み込みし直させる
      this.facilitySettingTab.refresh();

      this.showSuccessModal = true;
      await this.createView();
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.judgementErrorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 待機者ラベルを更新する
   */
  public async updateWaiterLabels(labels: Array<WaiterLabelSettingTableItem>): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      for (const label of labels) {
        const requestBody: WaiterLabelsPatchModel = this.createWaiterLabelsRequestBody(label);
        await this.waiterLabelsService.patch(userStore, label.id, requestBody);
      }

      this.isBusy = false;
      this.successModalTitle = "更新に成功しました。";
      this.showSuccessModal = true;

      await this.reflesh();
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        this.errorMessageInWaiterLabelsUpdate = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        await this.reflesh();
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 待機者ラベル更新のリクエストボディを作成する
   */
  public createWaiterLabelsRequestBody(label: WaiterLabelSettingTableItem): WaiterLabelsPatchModel {
    const colors: { color: string; bgColor: string } = this.getColors(label.colorId);

    return {
      name: label.name,
      description: label.description,
      color: colors.color,
      bgColor: colors.bgColor,
      use: label.use,
      updated: label.updated
    };
  }

  public getColors(colorId: number): { color: string; bgColor: string } {
    switch (colorId) {
      case 1: {
        return { bgColor: "#0091BF", color: "#FFFFFF" };
      }
      case 2: {
        return { bgColor: "#00AF3A", color: "#FFFFFF" };
      }
      case 3: {
        return { bgColor: "#DF5517", color: "#FFFFFF" };
      }
      case 4: {
        return { bgColor: "#DECB05", color: "#FFFFFF" };
      }
      case 5: {
        return { bgColor: "#F1396B", color: "#FFFFFF" };
      }
      case 6: {
        return { bgColor: "#70D9F9", color: "#000000" };
      }
      case 7: {
        return { bgColor: "#57E78F", color: "#000000" };
      }
      case 8: {
        return { bgColor: "#ECAB8F", color: "#000000" };
      }
      case 9: {
        return { bgColor: "#F1E583", color: "#000000" };
      }
      case 10: {
        return { bgColor: "#F7B4C6", color: "#000000" };
      }
      default: {
        return { bgColor: "#FFFFFF", color: "#000000" };
      }
    }
  }

  public gotoWaiterList(): void {
    this.$router.push(PcUrl.WaiterList);
    return;
  }
}
</script>

<style lang="scss" scoped>
#waiter-setting {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }
}
</style>
